/**
 * Parseur de flux `text/event-stream`.
 *
 * NOTE — copie conforme de `frontend/src/services/parseurSse.ts`. Les deux applications sont
 * des projets CRA independants, sans paquet partage : le code est duplique, mais le mecanisme
 * reste unique par construction. Toute correction ici doit etre reportee a l'identique dans
 * l'autre.
 *
 * `EventSource` faisait ce travail gratuitement. En passant à `fetch` + `ReadableStream` — seule
 * façon de porter un en-tête `Authorization` — on récupère un flux d'OCTETS, et tout ce que le
 * navigateur assurait doit être réécrit. C'est la brique risquée du chantier : mal faite, elle
 * perd des événements de manière intermittente, au gré du découpage réseau, ce qui ne se
 * diagnostique pas facilement.
 *
 * Trois pièges, chacun invisible sur un message bien formé :
 *
 * - **Le découpage réseau est arbitraire.** Un événement arrive fréquemment à cheval sur deux
 *   morceaux. Un `split('\n')` par morceau perd la moitié d'un événement sur deux. D'où le
 *   tampon, qui ne rend un événement qu'une fois la ligne vide rencontrée.
 * - **UTF-8 est multi-octets.** Un « é » peut être coupé entre deux morceaux. `TextDecoder` avec
 *   `{stream: true}` conserve les octets incomplets d'un appel à l'autre ; décoder chaque morceau
 *   isolément produirait des « � ».
 * - **Le séparateur d'événements est la LIGNE VIDE**, pas le retour à la ligne. Plusieurs lignes
 *   `data:` d'un même bloc se concatènent avec `\n`, conformément à la spécification.
 *
 * Le parseur ne connaît ni le réseau ni `fetch` : il reçoit des chaînes et rend des événements.
 * C'est ce qui permet de l'éprouver sur des découpages choisis, sans serveur.
 */

export interface EvenementSse {
    /** Nom de l'événement ; « message » par défaut, comme le veut la spécification. */
    type: string;
    /** Lignes `data:` du bloc, jointes par des retours à la ligne. */
    data: string;
    /** Dernier `id:` rencontré, s'il y en a un. */
    lastEventId?: string;
    /** Valeur de `retry:` en millisecondes, si le serveur en a envoyé une. */
    retry?: number;
}

/**
 * Découpe un flux SSE en événements, en mémorisant ce qui reste incomplet.
 *
 * Une instance suit un flux ; une reconnexion doit en créer une nouvelle, pour ne pas hériter
 * d'un tampon à moitié rempli par la connexion précédente.
 */
export class ParseurSse {
    private tampon = '';
    private dernierId: string | undefined;

    /**
     * Consomme un fragment de texte et rend les événements devenus complets.
     *
     * Un fragment peut ne rien produire (événement incomplet), en produire plusieurs, ou couper
     * un événement en deux — les trois cas sont normaux.
     */
    consommer(fragment: string): EvenementSse[] {
        // Normalisation des fins de ligne : la spécification accepte \r\n, \n et \r seul.
        this.tampon += fragment.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

        const evenements: EvenementSse[] = [];
        let separateur = this.tampon.indexOf('\n\n');

        while (separateur !== -1) {
            const bloc = this.tampon.slice(0, separateur);
            this.tampon = this.tampon.slice(separateur + 2);

            const evenement = this.analyserBloc(bloc);
            if (evenement) {
                evenements.push(evenement);
            }
            separateur = this.tampon.indexOf('\n\n');
        }

        return evenements;
    }

    /**
     * Analyse un bloc complet (sans sa ligne vide finale).
     *
     * @returns l'événement, ou `null` si le bloc ne portait aucune donnée — un commentaire
     *          (ligne commençant par « : », qu'on utilise souvent comme battement de cœur) ou un
     *          bloc ne contenant que des champs sans `data:` ne doit pas être remonté.
     */
    private analyserBloc(bloc: string): EvenementSse | null {
        let type = '';
        const donnees: string[] = [];
        let retry: number | undefined;
        let porteDesDonnees = false;

        for (const ligne of bloc.split('\n')) {
            // Ligne de commentaire : ignorée, elle sert à maintenir la connexion ouverte.
            if (ligne.startsWith(':')) {
                continue;
            }

            const separateur = ligne.indexOf(':');
            let champ: string;
            let valeur: string;

            if (separateur === -1) {
                // Une ligne sans « : » est un nom de champ à valeur vide.
                champ = ligne;
                valeur = '';
            } else {
                champ = ligne.slice(0, separateur);
                // Un unique espace après « : » fait partie de la syntaxe, pas de la valeur.
                valeur = ligne.slice(separateur + 1);
                if (valeur.startsWith(' ')) {
                    valeur = valeur.slice(1);
                }
            }

            switch (champ) {
                case 'event':
                    type = valeur;
                    break;
                case 'data':
                    donnees.push(valeur);
                    porteDesDonnees = true;
                    break;
                case 'id':
                    // La spécification écarte les id contenant un octet nul.
                    if (valeur.indexOf('\x00') === -1) {
                        this.dernierId = valeur;
                    }
                    break;
                case 'retry':
                    if (/^\d+$/.test(valeur)) {
                        retry = Number(valeur);
                    }
                    break;
                default:
                    // Champ inconnu : ignoré, comme l'exige la spécification.
                    break;
            }
        }

        if (!porteDesDonnees) {
            return null;
        }

        return {
            type: type || 'message',
            data: donnees.join('\n'),
            lastEventId: this.dernierId,
            retry,
        };
    }
}

/**
 * Lit un corps de réponse `fetch` et remet chaque événement au gestionnaire.
 *
 * Le `TextDecoder` est partagé par toute la lecture — c'est lui qui recolle les caractères UTF-8
 * coupés entre deux morceaux. En créer un par morceau réintroduirait le défaut.
 */
export const lireFluxSse = async (
    corps: ReadableStream<Uint8Array>,
    surEvenement: (evenement: EvenementSse) => void,
): Promise<void> => {
    const lecteur = corps.getReader();
    const decodeur = new TextDecoder('utf-8');
    const parseur = new ParseurSse();

    try {
        for (;;) {
            const {done, value} = await lecteur.read();
            if (done) {
                // Dernier appel sans `stream`, pour vider les octets encore retenus.
                const reste = decodeur.decode();
                if (reste) {
                    parseur.consommer(reste).forEach(surEvenement);
                }
                return;
            }
            parseur.consommer(decodeur.decode(value, {stream: true})).forEach(surEvenement);
        }
    } finally {
        // Libère la connexion même si l'appelant a interrompu la lecture.
        try {
            lecteur.releaseLock();
        } catch {
            // Le verrou peut déjà avoir été rendu par l'annulation : sans conséquence.
        }
    }
};
